import moment, { Moment } from '@e3dc-react/shell/Libs/moment'
import { ChartView } from 'src/Libs/chart-kit'
import { fetchBreakthrough } from '../api'
import { transformBreakthroughData } from '../utils/transformers'
import useSystemInfo from './use-system-info'
import { Breakthrough } from '../models'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { usePortalQuery } from 'src/Libs/api'

interface UseBreakthroughData {
  storageId: string
  from: Moment
  to: Moment
  view: ChartView
}

interface ReturnType {
  breakthroughData: Breakthrough[]
  isLoading: boolean
  hasWeatherBreakthrough: boolean
}

/**
 * Fetches breakthrough data for the given time range
 */
export const useFetchBreakthroughData = ({ storageId, from, to, view }: UseBreakthroughData): ReturnType => {
  const { systemInfo } = useSystemInfo(storageId)
  const authFetch = useAuthFetchWithResponse()
  const installationsTimezone = systemInfo?.storage.timezone

  const shouldFetch = (view === 'day' || view === 'week') && !!systemInfo

  const fromTimestamp = from.valueOf()
  const toTimestamp = to.valueOf()

  const { data, isLoading } = usePortalQuery({
    queryKey: ['breakthrough', storageId, fromTimestamp, toTimestamp, view],
    queryFn: () => fetchBreakthrough(storageId, fromTimestamp, toTimestamp, authFetch),
    select: data => transformBreakthroughData(data ?? [], installationsTimezone),
    enabled: shouldFetch,
  })

  const hasData = data && data.length > 0
  const hasWeatherBreakthrough =
    hasData &&
    data.some(breakthrough => {
      const breakthroughDate = moment(breakthrough.deratePowerAt)
      return breakthroughDate.isSame(from, 'day')
    })

  return {
    breakthroughData: data ?? [],
    isLoading: isLoading && shouldFetch,
    hasWeatherBreakthrough: hasWeatherBreakthrough ?? false,
  }
}
